/**
 * The Inspector's "When it holds" section (1.J stage 3, deliverable 31): the selected link's
 * window, whether it holds at NOW, "Dates disagree" against the other end's own date, and
 * "Change dates", which opens the validity form in place.
 */
import type { LinkV1 } from '@codex/contracts';
import { Button, StateBadge } from '@codex/ui';
import { useId, useState } from 'react';
import { usePlacements } from '../../when/calendar-context.js';
import { primaryPlacement } from '../../when/pill-model.js';
import { ValidityCells, validityWords, type WhenFacts } from '../validity/validity-cells.js';
import { ValidityForm } from '../validity/validity-form.js';
import { datesDisagree, targetRange } from '../validity/validity-model.js';

export function WhenItHolds({
  docId,
  link,
  otherId,
  otherTitle,
  otherTypeKey,
  facts,
}: {
  docId: string;
  link: LinkV1;
  otherId: string;
  otherTitle: string;
  otherTypeKey: string | null;
  facts: WhenFacts;
}) {
  const headingId = useId();
  const noteId = useId();
  const [editing, setEditing] = useState(false);
  const [button, setButton] = useState<HTMLButtonElement | null>(null);
  const placements = usePlacements(otherId);
  const primary = placements.data ? primaryPlacement(placements.data, otherTypeKey) : null;
  const disagree = datesDisagree(link, targetRange(primary));
  const words = validityWords(link, docId, facts);

  return (
    <section aria-labelledby={headingId} className="cx-links-editor__when">
      <h4 id={headingId} className="cx-links-tab__group">
        When it holds
      </h4>
      {words === null ? (
        <p className="cx-links-story__empty">This link has no dates.</p>
      ) : (
        <p className="cx-links-editor__window">
          <ValidityCells link={link} docId={docId} facts={facts} />
        </p>
      )}
      {disagree && (
        <span aria-describedby={noteId}>
          <StateBadge state={{ icon: 'warning', label: 'Dates disagree', tone: 'warning' }} />
          <span id={noteId} className="cx-visually-hidden">
            The link's dates and {otherTitle}'s date don't overlap.
          </span>
        </span>
      )}
      <Button ref={setButton} size="sm" hidden={editing} onClick={() => setEditing(true)}>
        Change dates
      </Button>
      {editing && (
        <ValidityForm
          link={link}
          docId={docId}
          returnTo={button}
          onClose={() => setEditing(false)}
        />
      )}
    </section>
  );
}
